import * as React from "react";
import { singletonHook } from "react-singleton-hook";

/**
 * The theme toggle, as a singleton hook.
 *
 * All it does is add or remove `dark` on <html> and remember the choice. Every colour in the app
 * comes from the semantic tokens in `styles/tailwind.css`, which are defined once on `:root` and
 * again under `.dark` - so flipping the class re-themes everything, with no per-component work and
 * nothing to keep in sync.
 *
 * `singletonHook` means every caller shares one piece of state, so the toggle in the header and the
 * skeleton theme in App.tsx can never disagree about which theme is active.
 *
 * This replaces components/DarkModeSwitch.tsx, which also exported a floating sun/moon button that
 * sat over the top-right corner of the page. The shell header owns that control now.
 */

const STORAGE_KEY = "dark-mode";

function readStoredPreference(): boolean {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "true");
    } catch {
        return true;
    }
}

function applyClass(isDark: boolean) {
    document.documentElement.classList.toggle("dark", isDark);
}

const darkModeFactory = (): [boolean, () => void] => {
    const [isDarkMode, setDarkMode] = React.useState(readStoredPreference);

    const toggleDarkMode = React.useCallback(() => {
        setDarkMode((current) => {
            const next = !current;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            applyClass(next);
            return next;
        });
    }, []);

    // The class has to be set on first render too, not only on toggle - otherwise a reload lands on
    // the light theme regardless of what was stored.
    React.useEffect(() => {
        applyClass(isDarkMode);
    }, [isDarkMode]);

    return [isDarkMode, toggleDarkMode];
};

export const useDarkMode = singletonHook<[boolean, () => void]>([true, () => undefined], darkModeFactory);

export default useDarkMode;
